<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useDownloadExport } from "@/modules/exports/composables/useDownloadExport";
import ExportDetailsDialog from "@/modules/exports/pages/ExportDetailsDialog.vue";
import MainBreadcrumbs from "@/ui/components/header/MainBreadcrumbs.vue";
import ExportListDO = App.Data.ExportListDO;
import { useApiClient } from "@/api/client";
import dayjs from "dayjs";
import ExportTypeChip from "@/ui/components/exports/ExportTypeChip.vue";

const route = useRoute();
const router = useRouter();
const { download } = useDownloadExport();

const detailsDialogIsVisible = ref(false);
const currentExportUuid = ref<string>();

const breadCrumbs = [{ title: "Exports", disabled: true, to: "/exports" }];

const isLoading = ref(false);
const items = ref<ExportListDO[]>([]);
const itemsPerPage = ref(10);
const totalItemsLength = ref(0);
const page = ref(1);

const headers = [
	{ title: "Created At", key: "created_at", sortable: true },
	{ title: "Type", key: "export_type", sortable: true },
	{ title: "File Type", key: "file_type", sortable: false },
	{ title: "Is Scheduled", key: "is_scheduled", sortable: false },
	{ title: "Status", key: "status", sortable: false },
	{ title: "Actions", key: "actions", align: "center", sortable: false },
];

const fileType = (fileName: string) => {
	return fileName.split(".").pop().toUpperCase();
};

const getStatusColor = (status: string) => {
	switch (status) {
		case "pending":
			return "primary";
		case "success":
			return "success";
		case "failed":
			return "error";
		default:
			return "primary";
	}
};

const openDetailsDialog = (uuid: string) => {
	currentExportUuid.value = uuid;
	detailsDialogIsVisible.value = true;
};

type SortItem = { key: string; order?: "asc" | "desc" };

const loadExports = async (options?: { sortBy?: SortItem[] }) => {
	isLoading.value = true;
	const sort = (options?.sortBy ?? [])
		.map(({ key, order }) => (order === "desc" ? `-${key}` : key))
		.join(",");
	const response = await useApiClient("exports").index({
		page: page.value,
		per_page: itemsPerPage.value,
		...(sort ? { sort } : {}),
	});
	if (response.isOk()) {
		items.value = response.value.data;
		totalItemsLength.value = response.value.total;
	}
	isLoading.value = false;
};

onMounted(() => {
	const uuid = route.params.uuid as string;

	if (!uuid) return;

	openDetailsDialog(uuid);

	if (route.query.download) {
		download(uuid);
	}

	router.replace({ name: "exports.index" });
});
</script>

<template>
	<ExportDetailsDialog
		:export-uuid="currentExportUuid"
		v-model="detailsDialogIsVisible"
	/>

	<main-breadcrumbs :breadcrumbs="breadCrumbs" />

	<v-card class="pa-4 pb-2">
		<v-data-table-server
			:items="items"
			:items-length="totalItemsLength"
			:loading="isLoading"
			:headers="headers"
			v-model:page="page"
			v-model:items-per-page="itemsPerPage"
			@update:options="loadExports"
		>
			<template v-slot:item.created_at="{ item }">
				{{ dayjs(item.created_at).format("DD MMM YYYY HH:mm") }}
			</template>

			<template v-slot:item.export_type="{ item }">
				<ExportTypeChip :export-type="item.export_type_name" />
			</template>

			<template v-slot:item.file_type="{ item }">
				<kbd class="bg-secondary">{{ fileType(item.file_name) }}</kbd>
			</template>

			<template v-slot:item.is_scheduled="{ item }">
				{{ item.is_scheduled ? "Yes" : "No" }}
			</template>

			<template v-slot:item.status="{ item }">
				<v-chip
					:color="getStatusColor(item.status)"
					label
					density="comfortable"
				>
					{{ item.status }}
				</v-chip>
			</template>

			<template v-slot:item.actions="{ item }">
				<v-btn
					size="30"
					variant="text"
					@click="download(item.uuid)"
					v-tooltip="'Download export'"
					:disabled="item.status !== 'success'"
					:data-testid="`download-export-${item.uuid}`"
				>
					<v-icon size="18" icon="fa-solid fa-download" />
				</v-btn>

				<v-btn
					size="30"
					variant="text"
					@click="openDetailsDialog(item.uuid)"
					v-tooltip="'View details'"
					:data-testid="`view-details-${item.uuid}`"
				>
					<v-icon size="18" icon="fa-light fa-circle-info" />
				</v-btn>
			</template>
		</v-data-table-server>
	</v-card>
</template>

<style scoped lang="scss"></style>
